import type { UploadResponse } from '@/types/sub-app-types'
import { axiosClient } from '@/utils/axios-client'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useFileStore = defineStore('fileStore', () => {
  const imagePreviewUrl = ref<string | null>(null)

  async function downloadFile(id: string): Promise<{ imageSrc: string; blob: Blob }> {
    const response = await axiosClient.request<Blob>({
      method: 'GET',
      url: `/storage/${id}/download`,
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })

    const disposition = response.headers['content-disposition'] as string
    const fileName = disposition?.match(/filename="(.+)"/)?.[1] ?? ''
    const ext = fileName.split('.').pop()?.toLowerCase()

    const mimeMap: Record<string, string> = {
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      webp: 'image/webp',
      gif: 'image/gif',
    }

    const mime = mimeMap[ext ?? ''] ?? 'image/png'
    const blob = new Blob([response.data], { type: mime })

    return { imageSrc: URL.createObjectURL(blob), blob }
  }

  async function uploadFile(file: File, isPublic: boolean = false) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await axiosClient.request<UploadResponse>({
      method: 'POST',
      url: `storage/upload`,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
      params: { public: isPublic },
    })

    return response.data
  }

  async function updateFile(file: File, fileId: string, isPublic: boolean = false) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await axiosClient.request<UploadResponse>({
      method: 'PATCH',
      url: `storage/${fileId}`,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
      params: { public: isPublic },
    })

    return response.data
  }

  async function deleteFile(id: string) {
    await axiosClient.request({
      method: 'DELETE',
      url: `storage/${id}`,
    })
  }

  return { imagePreviewUrl, downloadFile, uploadFile, updateFile, deleteFile }
})
